import { useQuery } from '@tanstack/react-query';
import { useInventorySource } from './InventorySource';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoney, fmtNumber, fmtDate, fmtBin } from './format';
import { c, t, sp, tbl } from './../../theme';

// Part-level rows across every cost center the user can see, ordered by
// exposure. The storeroom works by what needs doing, not by where it sits.
//
// There is no Action or Owner column, which is the honest gap: those need
// assignment and approval state that does not exist yet. Until then this is a
// list to work from, not a board that tracks whether the work happened — and
// it is labeled that way rather than implying otherwise.
export default function WorklistTable({ exception, ccId, classification, partCategory, band, partNumber, limit = 100 }) {
  const { api, queryKey } = useInventorySource();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKey('inventory-worklist', exception || 'any', ccId || 'all', classification || 'any', partCategory || 'any', band || 'any', partNumber || 'any', limit),
    queryFn:  () => api.get('/inventory/worklist', {
      params: {
        ...(exception ? { exception } : {}),
        ...(ccId ? { cc_id: ccId } : {}),
        ...(classification ? { classification } : {}),
        ...(partCategory ? { part_category: partCategory } : {}),
        ...(band ? { band } : {}),
        ...(partNumber ? { part_number: partNumber } : {}),
        limit,
      },
    }),
  });

  if (isLoading) return <div style={s.pad}><LoadingSpinner /></div>;
  if (error)     return <div style={s.pad}><div style={s.err}>{error.message}</div></div>;

  const items = data?.items || [];
  // "Everything is within its levels" is a real finding; "no part number
  // matched" is a search that found nothing. Reporting the first when the
  // second happened tells a reader their stock is healthy because they mistyped.
  if (!items.length) {
    return (
      <div style={s.empty}>
        {partNumber
          ? `No part number contains “${partNumber}” in this population.`
          : 'Nothing on this list — everything is within its levels.'}
      </div>
    );
  }

  return (
    <>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Part #</th>
              <th style={s.th}>Description</th>
              <th style={s.th}>Part Category</th>
              <th style={s.th}>Cost Center</th>
              {/* Aisle-Row-Bin, so the list can be walked in the storeroom
                  rather than looked up part by part. Rendered as one column
                  because the three are never useful apart. */}
              <th style={s.th}>Aisle / Row / Bin</th>
              <th style={s.thR}>On Hand</th>
              <th style={s.thR}>Min / Max</th>
              <th style={s.thR}>Exposure</th>
              <th style={s.th}>Last Issued</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const short  = Number(it.under_min || 0);
              const excess = Number(it.over_max || 0);
              return (
                <tr key={it.id} style={s.tr}>
                  <td style={s.td}>{it.part_number}</td>
                  <td style={s.tdDesc} title={it.description}>{it.description}</td>
                  <td style={s.td}>{it.part_category}</td>
                  {/* `code — name`, the form the rest of the board already
                      uses: the cost-center picker, the panel headings, and the
                      by-cost-center table's own first column. A second style
                      here would make the same entity look like two things on
                      one screen. Falls back to the bare code rather than
                      leaving a dangling dash. */}
                  <td style={s.td}>
                    {it.cost_center_name
                      ? `${it.cost_center_code} — ${it.cost_center_name}`
                      : it.cost_center_code}
                  </td>
                  <td style={s.tdLoc}>{fmtBin(it)}</td>
                  <td style={s.tdR}>{fmtNumber(it.current_quantity)}</td>
                  <td style={{ ...s.tdR, color: it.max_not_set ? c.textFaint : c.textStrong }}>
                    {fmtNumber(it.min_stock_level)} / {it.max_not_set ? 'not set' : fmtNumber(it.max_stock_level)}
                  </td>
                  <td style={{
                    ...s.tdR,
                    color: short ? c.dangerText : (excess ? c.warningText : c.textStrong),
                  }}>
                    {short ? `${fmtMoney(short)} short`
                      : excess ? `${fmtMoney(excess)} excess`
                        : fmtMoney(it.inventory_value)}
                  </td>
                  <td style={{ ...s.td, color: it.last_used_date ? c.textStrong : c.warningText }}>
                    {it.last_used_date ? fmtDate(it.last_used_date) : 'never'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data?.truncated && (
        <div style={s.truncated}>
          Showing the {fmtNumber(limit)} largest by exposure — there are more below this cut.
        </div>
      )}
    </>
  );
}

const s = {
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { ...tbl.th, whiteSpace: 'nowrap' },
  thR:    { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:     { ...tbl.tr },
  td:     { ...tbl.td, whiteSpace: 'nowrap' },
  tdR:    { ...tbl.tdR },
  tdDesc: { ...tbl.td, maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // Tabular figures so bin codes line up as a column of locations to walk.
  tdLoc:  { ...tbl.td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: c.textMuted },
  pad:    { padding: sp[5] },
  empty:  { padding: sp[8], textAlign: 'center', ...t.bodySm, color: c.textFaint },
  err:    { ...t.bodySm, color: c.danger },
  truncated: {
    padding: `${sp[2]} ${sp[4]}`, ...t.micro, color: c.textFaint,
    borderTop: `1px solid ${c.border}`, background: c.surfaceSunk,
  },
};
